import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onClick"],
  _excluded2 = ["highlighted", "aria-disabled", "isIndeterminate", "isSelected", "id", "itemComponent", "toggleable", "_useButton", "_useDropdownText", "onMouseEnter", "onToggle", "enableKeyboardScrollingNav", "message", "option", "search"];
import { cloneElement, forwardRef, isValidElement } from 'react';
import { Checkbox } from '../Checkbox';
import { stopPropagationHandler } from '../../utils/DomEvents';
import { TFlex } from '../TFlex';
import { LoadingSpinner } from '../LoadingSpinner';
import getComponentName from 'react-utils/getComponentName';
import { warnIfFragment } from '../../utils/warnIfFragment';
import { Image } from '../Image';
import { Tag } from '../Tag';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { Text } from '../../../components/Text';
import styled from 'foundations-theming/utils';
import { View } from '../../../components/View';
import { SpinnerOuter } from '../LoadingSpinner/LoadingSpinner';
import { UnstyledButton } from '../Button/PrivateButton';
import { useIsDisplayOptimized } from '../../context/DisplayOptimizedContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BLANK_LABEL = '\u00A0';
const StyledButtonWrapper = styled.span.withConfig({
  displayName: "TypeaheadResultsItem__StyledButtonWrapper",
  componentId: "sc-151rpex-0"
})(() => {
  return {
    display: 'block',
    width: '100%'
  };
});
export const StyledContentLabel = styled.span.withConfig({
  displayName: "TypeaheadResultsItem__StyledContentLabel",
  componentId: "sc-151rpex-1"
})(() => {
  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [`& ${SpinnerOuter}`]: {
      transform: 'scale(0.75)'
    }
  };
});
export const StyledContentWrapper = styled.span.withConfig({
  displayName: "TypeaheadResultsItem__StyledContentWrapper",
  componentId: "sc-151rpex-2"
})(({
  $toggleable,
  theme
}) => {
  return Object.assign({
    display: 'flex',
    alignItems: 'center'
  }, $toggleable && {
    justifyContent: 'center',
    paddingInlineEnd: theme.space['400'],
    fontSize: theme.typography['body-100'].fontSize
  });
});
export const StyledItem = styled.li.withConfig({
  displayName: "TypeaheadResultsItem__StyledItem",
  componentId: "sc-151rpex-3"
})(({
  $highlighted,
  $message,
  option,
  isCustomComponent,
  theme
}) => {
  return Object.assign({
    '&:hover:not([aria-disabled="true"])': {
      cursor: 'pointer'
    }
  }, isCustomComponent && {
    display: 'block'
  }, $message && {}, $highlighted && {
    backgroundColor: theme.color['fill-secondary-subtle'],
    [`& ${UnstyledButton}`]: {
      backgroundColor: `${theme.color['fill-secondary-subtle']} !important`
    }
  }, option.disabled && {
    cursor: 'not-allowed',
    color: theme.color['text-primary-disabled'],
    backgroundColor: theme.color['fill-surface-default'],
    borderColor: theme.color['fill-primary-disabled']
  });
});
const getLoadingIndicator = computedButtonText => {
  return /*#__PURE__*/_jsxs(TFlex, {
    align: "center",
    children: [computedButtonText, /*#__PURE__*/_jsx(LoadingSpinner, {
      marginBlock: "0",
      marginInline: "0",
      marginInlineStart: "100",
      size: "xs",
      use: "tertiary"
    })]
  });
};
const getLabel = ({
  option: {
    buttonText,
    dropdownText,
    loading,
    text,
    value
  },
  _useDropdownText
}) => {
  const fallbackText = text || (value == null ? '' : value.toString());
  if (_useDropdownText) {
    return dropdownText == null ? fallbackText : dropdownText;
  }
  const computedButtonText = buttonText == null ? fallbackText : buttonText;
  if (loading) {
    return getLoadingIndicator(`${computedButtonText}`);
  } else {
    return computedButtonText;
  }
};
const getTitle = ({
  option: {
    text,
    title,
    value
  }
}) => {
  if (title === '') {
    return undefined;
  }
  return title || text || (value == null ? '' : value.toString());
};
const renderHelp = ({
  option: {
    disabled,
    help
  }
}) => {
  if (help) {
    return /*#__PURE__*/_jsx(Text, {
      as: "small",
      marginBlockStart: "-50",
      paddingBlockStart: "0",
      paddingInline: "500",
      paddingBlockEnd: "200",
      whiteSpace: "normal",
      wordBreak: "break-all",
      display: "block",
      typography: "detail-200",
      color: disabled ? 'text-primary-disabled' : 'text-core-subtle',
      children: help
    });
  }
  return null;
};
const renderLabel = ({
  search,
  computedLabel
}) => {
  if (!computedLabel) {
    return BLANK_LABEL;
  }
  if (!search || typeof computedLabel !== 'string') {
    return computedLabel;
  }
  const searchResult = search.exec(computedLabel);
  if (!searchResult) {
    return computedLabel;
  }
  const match = searchResult[0];
  const head = computedLabel.slice(0, searchResult.index);
  const tail = computedLabel.slice(searchResult.index + match.length);
  return /*#__PURE__*/_jsxs("span", {
    children: [head, /*#__PURE__*/_jsx("span", {
      children: match
    }), tail]
  });
};
const renderItemImage = ({
  option: {
    avatar,
    icon,
    iconColor,
    imageUrl
  },
  isDisplayOptimized
}) => {
  if (avatar) {
    const isValidAvatarElement = /*#__PURE__*/isValidElement(avatar);
    const size = isValidAvatarElement ? avatar.props.size : undefined;
    // @ts-expect-error TODO: fix this soon
    const isUIAvatar = getComponentName(avatar) === 'UIAvatar'; // TODO: check if this is still UIAvatar

    warnIfFragment(avatar, 'TypeaheadResultsItem', 'avatar');
    const avatarEl = isValidAvatarElement ? /*#__PURE__*/cloneElement(avatar, Object.assign({}, isUIAvatar ? {
      size: 'responsive'
    } : {
      size,
      shape: 'circle'
    })) : null;
    return /*#__PURE__*/_jsx(View, {
      display: "inline-block",
      maxInlineSize: "24px",
      marginInlineEnd: isDisplayOptimized ? '100' : '400',
      marginBlockStart: "-50",
      marginInlineStart: isDisplayOptimized ? '0' : '-100',
      children: avatarEl
    });
  }
  if (icon) {
    const Icon = icon;
    return /*#__PURE__*/_jsx(View, {
      display: "inline-block",
      marginBlockStart: "100",
      marginInlineEnd: "300",
      children: /*#__PURE__*/_jsx(Icon, {
        size: "200",
        color: iconColor
      })
    });
  }
  if (imageUrl) {
    return /*#__PURE__*/_jsx(Image, {
      src: imageUrl,
      shape: "circle",
      responsive: false,
      blockSize: "24px",
      marginInlineEnd: "400",
      marginBlockStart: "-50",
      marginInlineStart: "-100"
    });
  }
  return null;
};
const renderTag = ({
  option: {
    tag
  }
}) => {
  if (!tag) {
    return null;
  }
  return /*#__PURE__*/_jsx(Tag, {
    inline: true,
    use: tag.use,
    children: tag.text
  });
};
const renderBadge = ({
  option: {
    badge
  }
}) => {
  if (!badge) {
    return null;
  }
  if ( /*#__PURE__*/isValidElement(badge)) {
    warnIfFragment(badge, 'TypeaheadResultsItem', 'badge');
    return /*#__PURE__*/cloneElement(badge, {
      marginInlineStart: '200',
      marginBlockStart: '-50'
    });
  }
  if (typeof badge === 'object' && 'use' in badge) {
    return /*#__PURE__*/_jsx(Badge, {
      marginInlineStart: "200",
      marginBlockStart: "-50",
      use: badge.use,
      children: badge.text
    });
  }
  return null;
};
const renderContent = ({
  renderedItemImage,
  renderedLabel,
  renderedTag,
  renderedBadge,
  toggleable
}) => {
  return /*#__PURE__*/_jsxs(StyledContentWrapper, {
    $toggleable: toggleable,
    children: [renderedItemImage, ' ', /*#__PURE__*/_jsx(StyledContentLabel, {
      "data-option-text": true,
      children: renderedLabel
    }), ' ', renderedTag, " ", renderedBadge]
  });
};
const renderButton = _ref => {
  let {
      option,
      rest: {
        onClick
      },
      computedTitle,
      renderedContent,
      renderedHelp
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref.rest, _excluded);
  return (
    /*#__PURE__*/
    // Keyboard events handled by parent component using aria-activedescendant pattern
    _jsxs(StyledButtonWrapper, {
      onClick: onClick,
      children: [/*#__PURE__*/_jsx(Button, Object.assign({}, rest, {
        paddingBlock: "200",
        paddingInline: "500",
        tabIndex: -1,
        title: computedTitle,
        use: "unstyled",
        disabled: option.disabled,
        color: option.disabled ? 'text-primary-disabled' : 'inherit',
        children: renderedContent
      })), renderedHelp]
    })
  );
};
const renderCheckbox = ({
  isIndeterminate,
  isSelected,
  onToggle,
  option,
  renderedContent,
  renderedHelp
}) => {
  return /*#__PURE__*/_jsxs("label", {
    // Route clicks on help text to checkbox
    onMouseDown: stopPropagationHandler // Prevent double-toggle
    ,
    children: [/*#__PURE__*/_jsx(Checkbox, {
      paddingBlock: "200",
      paddingInline: "500",
      checked: isSelected,
      disabled: option.disabled || isSelected && option.clearableValue === false,
      id: `checkbox-${option.value}`,
      indeterminate: isIndeterminate,
      onChange: e => onToggle && onToggle(e),
      children: renderedContent
    }), renderedHelp]
  });
};
export const TypeaheadResultsItem = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
      highlighted,
      'aria-disabled': ariaDisabled = false,
      isIndeterminate,
      isSelected,
      id,
      itemComponent: Item = 'li',
      toggleable = false,
      _useButton = true,
      _useDropdownText = true,
      onMouseEnter,
      onToggle,
      enableKeyboardScrollingNav,
      message,
      option,
      search
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const isDisplayOptimized = useIsDisplayOptimized();

  // @ts-expect-error Escape hatch for dividers??
  if (option.rawNode) return option.rawNode;
  const computedAriaSelected = !!highlighted;
  const computedTitle = getTitle({
    option
  });
  const computedLabel = getLabel({
    option,
    _useDropdownText
  });
  const renderedItemImage = renderItemImage({
    option,
    isDisplayOptimized
  });
  const renderedLabel = renderLabel({
    search,
    computedLabel
  });
  const renderedTag = renderTag({
    option
  });
  const renderedBadge = renderBadge({
    option
  });
  const renderedHelp = renderHelp({
    option
  });
  let renderedContent = renderContent({
    renderedItemImage,
    renderedLabel,
    renderedTag,
    renderedBadge,
    toggleable
  });

  // If _useButton={true}, wrap content in a button
  renderedContent = _useButton ? renderButton({
    option,
    rest,
    computedTitle,
    renderedContent,
    renderedHelp
  }) : renderedContent;

  // If toggleable={true}, wrap content in a checkbox
  renderedContent = toggleable ? renderCheckbox({
    isIndeterminate,
    isSelected,
    onToggle,
    option,
    renderedContent,
    renderedHelp
  }) : renderedContent;
  if (typeof Item === 'function') {
    // Fix for issue #11303 (https://git.hubteam.com/HubSpot/UIComponents/issues/11303)
    if (enableKeyboardScrollingNav) {
      return /*#__PURE__*/_jsx(StyledItem, Object.assign({
        as: 'li'
      }, rest, {
        "aria-selected": computedAriaSelected,
        "aria-disabled": option.disabled || ariaDisabled || undefined,
        id: id,
        onMouseEnter: onMouseEnter,
        "data-option-value": option.value,
        option: option,
        $highlighted: highlighted,
        $message: message,
        role: "option",
        ref: ref,
        children: /*#__PURE__*/_jsx(Item, {
          option: option,
          children: renderedContent
        })
      }));
    }
    return /*#__PURE__*/_jsx(StyledItem, Object.assign({
      as: Item
    }, rest, {
      "aria-selected": computedAriaSelected,
      "aria-disabled": option.disabled || ariaDisabled || undefined,
      isCustomComponent: true,
      id: id,
      onMouseEnter: onMouseEnter,
      option: option,
      $highlighted: highlighted,
      $message: message,
      "data-option-value": option.value,
      role: "option",
      ref: ref,
      children: renderedContent
    }));
  }
  return /*#__PURE__*/_jsx(StyledItem, Object.assign({
    as: Item
  }, rest, {
    option: option,
    $highlighted: highlighted,
    $message: message,
    "aria-selected": computedAriaSelected,
    "aria-disabled": option.disabled || ariaDisabled || undefined,
    "data-option-value": option.value,
    id: id,
    onMouseEnter: onMouseEnter,
    role: "option",
    ref: ref,
    "data-typeahead-item": true,
    children: renderedContent
  }));
});
TypeaheadResultsItem.displayName = 'TypeaheadResultsItem';